import { openPanel as _openManageContentPanelV2 } from '../../panels/manage-content-panel-V2/openPanel'
import { openConnectPanel } from '../../panels/connection-config-panel/openPanel'
import { PagingMode } from '@wix/wix-data-schema-types'
import {
  getPagingMode,
  listAllControllers,
  ComponentTypes,
} from '@wix/wix-data-client-common'
import moveBackgroundConnection from './moveBackgroundConnection'
import {
  createDeferred,
  createEditorSdkProxy,
} from '@wix/wix-data-client-common-standalone'

const createPublicApi = () => {
  let editorSdkProxy = null
  let i18n = null
  let editorType = null
  let collectionsApi = null
  let appApi = null
  let experimentsManager = null
  let logger = null
  let hostname = null
  let safeUserPreferences = null
  let features = null
  const apiReady = createDeferred()

  return {
    init({
      editorSDK,
      token,
      i18n: _i18n,
      editorType: _editorType,
      collectionsApi: _collectionsApi,
      appApi: _appApi,
      experimentsManager: _experimentsManager,
      logger: _logger,
      hostname: _hostname,
      safeUserPreferences: _safeUserPreferences,
      features: _features,
    }) {
      editorSdkProxy = createEditorSdkProxy(editorSDK, token)
      i18n = _i18n
      editorType = _editorType
      collectionsApi = _collectionsApi
      appApi = _appApi
      experimentsManager = _experimentsManager
      logger = _logger
      hostname = _hostname
      safeUserPreferences = _safeUserPreferences
      features = _features
      apiReady.resolve()
    },

    api: {
      createDataset: (...params) => appApi.createDataset(...params),
      connectBindingSection: (...params) =>
        appApi.connectBindingSection(...params),
      setViewerStatusMessage: async viewerStatusMessage => {
        await apiReady.promise
        await appApi.setViewerStatusMessage(viewerStatusMessage)
      },
      openConnectPanel: async ({ componentRef, origin = 'public_api' }) => {
        if (features.inspectorXP) {
          return appApi.openInspectorPanel({
            view: 'Connect',
            componentRef,
            initiator: 'manually',
            // origin,
          })
        }

        return openConnectPanel(
          {
            hostname,
            appApi,
            editorSdkProxy,
            experimentsManager,
            logger,
            i18n,
            editorType,
            initiator: 'manually',
            origin,
          },
          { componentRef },
        )
      },
      openManageContentPanel({ componentRef, role }) {
        return _openManageContentPanelV2({
          appApi,
          collectionsApi,
          componentRef,
          editorSdkProxy,
          hostname,
          i18n,
          role,
          logger,
          safeUserPreferences,
          editorType,
        })
      },
      hideDatasets: async controllerRefs => {
        controllerRefs =
          controllerRefs ??
          (await listAllControllers(editorSdkProxy)).map(
            ({ controllerRef }) => controllerRef,
          )

        await appApi.hideControllers(controllerRefs)
      },
      async isDatasetUsingCursors({ controllerRef }) {
        let config
        try {
          const dataset = await appApi.fetchDataset(controllerRef)
          config = dataset.config
        } catch {
          throw new Error(`Dataset ${controllerRef} is not found`)
        }

        let collection
        try {
          collection = await collectionsApi.get(config.collectionId)
        } catch {
          throw new Error(
            `Dataset's collection ${config.collectionId} is not found`,
          )
        }

        const pagingMode = getPagingMode({
          schema: collection,
          cursorPagingPreferred: config.cursor,
        })

        return pagingMode === PagingMode.Cursor
      },
      async moveConnections({ source, target }) {
        const [
          { connections: sourceConnections, sdkType: sourceSdkType },
          { connections: targetConnections, sdkType: targetSdkType },
        ] = await editorSdkProxy.components.get({
          componentRefs: [source, target],
          properties: ['connections', 'sdkType'],
        })

        const fromColumnToStrip =
          sourceSdkType === ComponentTypes.Column &&
          targetSdkType === ComponentTypes.StripColumnsContainer
        const fromStripToColumn =
          sourceSdkType === ComponentTypes.StripColumnsContainer &&
          targetSdkType === ComponentTypes.Column

        if (fromColumnToStrip || fromStripToColumn) {
          await moveBackgroundConnection({
            editorSdkProxy,
            sourceConnections,
            targetConnections,
            source,
            target,
            targetIsStrip: fromColumnToStrip,
          })
        } else {
          throw new Error(
            `Incompatible component types: cannot move connections from ${sourceSdkType} to ${targetSdkType}`,
          )
        }
      },
    },
  }
}

export { createPublicApi }
